// ------------------ IMPORTS -------------------

import React, {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
} from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

// Components
import { Popover } from "@lockerpm/design";
import { ButtonV2 } from "#src/commonV2/Button";
import { Clock } from "@untitled-ui/icons-react";

// ------------------ TYPES -------------------

interface ScrollColumnProps {
  values: number[];
  selected: number;
  onSelect: (value: number) => void;
  borderRight?: boolean;
}

interface ScrollColumnRef {
  scrollToSelected: () => void;
}

export interface TimeInDayPickerProps {
  value?: number; // seconds from 0 to 24 * 60 * 60
  setValue?: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
}

// ------------------ UTILITIES -------------------

const generateNumbers = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

const formatTimeString = (
  hour: number,
  minute: number,
  second: number
): string =>
  `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}:${second.toString().padStart(2, "0")}`;

// Convert milliseconds (0 to 24*60*60*1000) to time components
const secondsToTime = (
  sec: number
): { hour: number; minute: number; second: number } => {
  const totalSeconds = sec;
  const hour = Math.floor(totalSeconds / 3600) % 24;
  const minute = Math.floor((totalSeconds % 3600) / 60);
  const second = totalSeconds % 60;
  return { hour, minute, second };
};

// Convert time components to milliseconds
const timeToSeconds = (
  hour: number,
  minute: number,
  second: number
): number => {
  return hour * 3600 + minute * 60 + second;
};

export const secondsToFormattedTime = (sec: number): string => {
  const { hour, minute, second } = secondsToTime(sec);
  return formatTimeString(hour, minute, second);
};

export function timestampToSeconds(timestamp: number | string | Date): number {
  const date = new Date(timestamp);
  return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

// ------------------ CHILD COMPONENTS -------------------

const ScrollColumn = forwardRef<ScrollColumnRef, ScrollColumnProps>(
  ({ values, selected, onSelect, borderRight }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        scrollToSelected: () => {
          if (containerRef.current) {
            const selectedIndex = values.indexOf(selected);
            if (selectedIndex !== -1) {
              const itemHeight = 44; // h-10 (40px) + my-1 (4px margin top/bottom)
              const scrollTop = selectedIndex * itemHeight;
              containerRef.current.scrollTo({
                top: scrollTop,
                behavior: "smooth",
              });
            }
          }
        },
      }),
      [selected, values]
    );

    return (
      <div
        ref={containerRef}
        className={[
          "flex-1 overflow-y-scroll h-72 text-center [&::-webkit-scrollbar]:hidden",
          borderRight ? "border-r border-gray-v2-200" : "",
        ].join(" ")}
        style={{
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE and Edge
        }}
      >
        {values.map((value) => (
          <div
            key={value}
            className={[
              "cursor-pointer text-sm flex justify-center items-center text-center rounded-full overflow-hidden mx-auto",
              selected === value
                ? "bg-primary text-white font-semibold"
                : "text-gray-v2-700",
            ].join(" ")}
            style={{ height: 40, width: 40, marginTop: 4, marginBottom: 4 }} // Required fixed values for scrolling
            onClick={() => onSelect(value)}
          >
            {value.toString()}
          </div>
        ))}
      </div>
    );
  }
);

ScrollColumn.displayName = "ScrollColumn";

// ------------------ MAIN COMPONENT -------------------

export const TimeInDayPicker: React.FC<TimeInDayPickerProps> = ({
  value,
  setValue,
  placeholder = "Select Time",
  disabled = false,
}) => {
  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });

  // ------------------ HOOKS -------------------

  const [visible, setVisible] = useState<boolean>(false);
  const hourRef = useRef<ScrollColumnRef>(null);
  const minuteRef = useRef<ScrollColumnRef>(null);
  const secondRef = useRef<ScrollColumnRef>(null);

  // ------------------ DATA -------------------

  const initialTime =
    value !== undefined
      ? secondsToTime(value)
      : {
          hour: dayjs().hour(),
          minute: dayjs().minute(),
          second: dayjs().second(),
        };

  const [hour, setHour] = useState<number>(initialTime.hour);
  const [minute, setMinute] = useState<number>(initialTime.minute);
  const [second, setSecond] = useState<number>(initialTime.second);

  // ------------------ COMPUTED -------------------

  const formattedTime = formatTimeString(hour, minute, second);
  const displayText = useMemo(() => {
    if (value === undefined) return placeholder;
    return secondsToFormattedTime(value);
  }, [value, placeholder]);

  // ------------------ EFFECTS -------------------

  // Sync internal state when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      const timeFromValue = secondsToTime(value);
      setHour(timeFromValue.hour);
      setMinute(timeFromValue.minute);
      setSecond(timeFromValue.second);
    }
  }, [value]);

  // ------------------ METHODS -------------------

  const handleHourSelect = useCallback((value: number): void => {
    setHour(value);
    setTimeout(() => hourRef.current?.scrollToSelected(), 0);
  }, []);

  const handleMinuteSelect = useCallback((value: number): void => {
    setMinute(value);
    setTimeout(() => minuteRef.current?.scrollToSelected(), 0);
  }, []);

  const handleSecondSelect = useCallback((value: number): void => {
    setSecond(value);
    setTimeout(() => secondRef.current?.scrollToSelected(), 0);
  }, []);

  const handleNow = useCallback((): void => {
    const now = dayjs();
    setHour(now.hour());
    setMinute(now.minute());
    setSecond(now.second());

    // Scroll to selected values after state update
    setTimeout(() => {
      hourRef.current?.scrollToSelected();
      minuteRef.current?.scrollToSelected();
      secondRef.current?.scrollToSelected();
    }, 0);
  }, []);

  const handleApply = useCallback((): void => {
    const seconds = timeToSeconds(hour, minute, second);
    setValue?.(seconds);
    setVisible(false);
  }, [hour, minute, second, setValue]);

  const handleCancel = useCallback((): void => {
    // Reset to initial values
    if (value !== undefined) {
      const resetTime = secondsToTime(value);
      setHour(resetTime.hour);
      setMinute(resetTime.minute);
      setSecond(resetTime.second);
    }
    setVisible(false);
  }, [value]);

  const handleVisibilityChange = useCallback(
    (open: boolean): void => {
      if (!disabled) {
        setVisible(open);

        // Scroll to selected values when popover opens
        if (open && value !== undefined) {
          const timeFromValue = secondsToTime(value);
          setHour(timeFromValue.hour);
          setMinute(timeFromValue.minute);
          setSecond(timeFromValue.second);

          // Scroll after state update
          setTimeout(() => {
            hourRef.current?.scrollToSelected();
            minuteRef.current?.scrollToSelected();
            secondRef.current?.scrollToSelected();
          }, 100);
        }
      }
    },
    [disabled, value]
  );

  // ------------------ RENDER -------------------

  const popoverContent = (
    <div className="w-64 v2">
      {/* Time Display Header */}
      <div className="flex justify-between items-center gap-3 mb-4">
        <div className="text-md text-gray-v2-900 py-2 px-3 rounded-lg border border-gray-v2-300 shadow-xs flex-1">
          {formattedTime}
        </div>

        <ButtonV2
          size="md"
          variant="secondary"
          color="gray"
          onClick={handleNow}
        >
          {t("modal.addAndEdit.time_frame.now")}
        </ButtonV2>
      </div>
      {/* Time Display Header End */}

      {/* Time Selector Columns */}
      <div className="flex gap-2 mb-2">
        <ScrollColumn
          ref={hourRef}
          values={generateNumbers(0, 23)}
          selected={hour}
          onSelect={handleHourSelect}
          borderRight
        />
        <ScrollColumn
          ref={minuteRef}
          values={generateNumbers(0, 59)}
          selected={minute}
          onSelect={handleMinuteSelect}
          borderRight
        />
        <ScrollColumn
          ref={secondRef}
          values={generateNumbers(0, 59)}
          selected={second}
          onSelect={handleSecondSelect}
        />
      </div>
      {/* Time Selector Columns End */}

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-gray-200 mb-1">
        <ButtonV2
          variant="secondary"
          color="gray"
          size="md"
          onClick={handleCancel}
          className="flex-1"
        >
          {t("modal.addAndEdit.time_frame.cancel")}
        </ButtonV2>
        <ButtonV2 size="md" onClick={handleApply} className="flex-1">
          {t("modal.addAndEdit.time_frame.apply")}
        </ButtonV2>
      </div>
      {/* Action Buttons End */}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={visible}
      onOpenChange={handleVisibilityChange}
    >
      <ButtonV2
        variant="secondary"
        color="gray"
        size="md"
        disabled={disabled}
        className="flex-1"
        contentFloatLeft
      >
        <Clock className="h-5 w-5" />
        <span>{displayText}</span>
      </ButtonV2>
    </Popover>
  );
};
